import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const attachmentSchema = z.object({
  file: z.custom<File>(),
  name: z.string(),
  _id: z.string().optional(),
});

const lessonFormSchema = z
  .object({
    title: z.string().min(3, "O título deve ter no mínimo 3 caracteres"),
    duration: z.number().optional(),
    description: z
      .string()
      .min(3, "A descrição deve ter no mínimo 3 caracteres")
      .max(500, "A descrição deve ter no máximo 500 caracteres"),
    isYoutubeVideo: z.boolean(),
    videoUrl: z.string().optional(),
    videoFile: z.custom<File>().optional(),
    thumbnailFile: z.custom<File>().optional(),
    files: z.array(attachmentSchema),
  })
  .superRefine((data, ctx) => {
    if (data.isYoutubeVideo && !data.videoUrl) {
      ctx.addIssue({
        code: "custom",
        path: ["videoUrl"],
        message: "O link do vídeo do YouTube é obrigatório.",
      });
    } else if (!data.isYoutubeVideo && !data.videoFile) {
      ctx.addIssue({
        code: "custom",
        path: ["videoFile"],
        message: "O arquivo de vídeo é obrigatório.",
      });
    }
  });

export type AttachmentFormValues = z.infer<typeof attachmentSchema>;
export type LessonFormValues = z.infer<typeof lessonFormSchema>;

const initialData: LessonFormValues = {
  title: "",
  duration: 0,
  description: "",
  isYoutubeVideo: false,
  videoUrl: "",
  thumbnailFile: undefined,
  files: [],
  videoFile: undefined,
};

interface UseClassFormProps {
  getDefaultValue?: LessonFormValues | undefined;
}

export const useClassForm = ({ getDefaultValue }: UseClassFormProps) => {
  const form = useForm({
    resolver: zodResolver(lessonFormSchema),
    defaultValues: async () => {
      if (getDefaultValue) {
        return getDefaultValue;
      }
      return initialData;
    },
  });

  useEffect(() => {
    if (getDefaultValue) {
      form.reset(getDefaultValue);
    }
  }, [getDefaultValue, form.reset]);

  return form;
};
